/// <amd-module name="Analytics/Boards/CumulativeFlowDiagramQueries/LowestLevelVisibleBoardQuery" />
import * as Resources from "Analytics/Boards/CumulativeFlowDiagramQueries/Resources";
import { ICacheableQuery } from "Reporting/Common/ICacheableQuery";
import { IVssPageContext } from "VSS/Platform/Context";
import { Board, BoardsQuery } from "./BoardsQuery";

// Exporting for consumers of this query
export { Board } from "./BoardsQuery";

/**
 * Gets the visible board with the lowest-level backlog for a given team.
 * It's confusing, but the lowest-level backlog has the highest board level value.
 *      For example:
 *          Epics: 0
 *          Features: 1
 *          User Stories: 2
 *      User Stories is the lowest-level backlog because Features contain User Stories and Epics contain Features, but
 *      User Stories has the largest board level number.
 *      If the User Stories board isn't visible, then the visible board with the next lowest-level backlog,
 *      Features in this example, is returned instead.
 * @param project ID or name of a project.
 * @param teamId ID of a team within the given project.
 * @returns the visible board with the lowest backlog level for the given team.
 */
export class LowestLevelVisibleBoardQuery implements ICacheableQuery<Board> {
    private boardsQuery: BoardsQuery;
    constructor(command: string, project: string, teamId: string) {
        this.boardsQuery = new BoardsQuery(command, project, teamId);
    }

    public async runQuery(pageContext: IVssPageContext): Promise<Board> {
        // By running BoardsQuery using this pattern, its result is not cached, so consumers
        // of BoardsQuery won't gain any advantage from LowestLevelVisibleBoardQuery.
        // This pattern should not be copied elsewhere without considering this.
        const boards = await this.boardsQuery.runQuery(pageContext);

        const visibleBoards = boards.filter(b => b.IsBoardVisible);
        if (visibleBoards.length === 0) {
            throw new Error(Resources.Error_FailedToFindAVisibleBoard);
        }

        const sortedBoards = visibleBoards.sort(function(a, b) {
            return b.BoardLevel - a.BoardLevel;
        });

        return sortedBoards[0];
    }

    public getKey(): string {
        return `LowestLevelVisibleBoardQuery-${this.boardsQuery.getKey()}`;
    }
}
